import { Link } from 'react-router-dom';
import { Body2, Button, Card, Subtitle1, makeStyles, tokens } from '@fluentui/react-components';
import { BookOpenRegular } from '@fluentui/react-icons';

import { ConfidenceBadge, FindingAssignment, FindingDocuments, FindingStatusBadge, SeverityBadge } from '../shared';
import { getFindingPath } from '../../helpers';
import { TFindingState } from '../../types';

const useStyles = makeStyles({
  card: {
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalXXL,
  },
  titleLink: {
    color: 'inherit',
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' },
  },
  footer: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: tokens.spacingVerticalM,
  },
});

type TProps = { finding: TFindingState; reviewId: string; productId?: string };

export const FindingCard = ({ finding, reviewId, productId }: TProps) => {
  const classes = useStyles();
  const path = getFindingPath(reviewId, finding.id, productId);

  return (
    <Card className={classes.card} size="large">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <SeverityBadge severity={finding.severity} />
          <ConfidenceBadge />
          <Body2 className="text-neutralFg-3">{finding.category}</Body2>
        </div>
        <Link to={path}>
          <Button appearance="secondary" size="large">
            View finding
          </Button>
        </Link>
      </div>

      <Link to={path} className={classes.titleLink}>
        <Subtitle1>{finding.title}</Subtitle1>
      </Link>

      <FindingDocuments documents={finding.affectedDocuments} limit={2} />

      {finding.reviewBasis?.length ? (
        <div className="flex items-start gap-2 text-neutralFg-3">
          <BookOpenRegular className="text-xl shrink-0 mt-0.5" />
          <div className="flex flex-col gap-1">
            {finding.reviewBasis.map((basis) => (
              <Body2 key={basis.id}>{basis.reference ? `${basis.label}, ${basis.reference}` : basis.label}</Body2>
            ))}
          </div>
        </div>
      ) : null}

      <div className={`${classes.footer} flex flex-wrap items-center justify-between gap-3`}>
        <FindingStatusBadge status={finding.status} size="large" />
        {finding.assignee || finding.dueDate ? <FindingAssignment finding={finding} /> : null}
      </div>
    </Card>
  );
};
